<template>
  <div class="donations-page">
    <!-- Header band -->
    <div class="band band-hero">
      <div class="band-content">
        <div class="page-header">
          <div class="brand-header">
            <router-link :to="`/for/${slug}`">
              <PassingOnLogo class="brand-logo-svg" />
            </router-link>
          </div>
          <ProfileMenu v-if="isAuthenticated" />
        </div>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-6" />
        <v-alert v-if="error" type="error" variant="tonal" class="mb-6">{{ error }}</v-alert>
      </div>
    </div>

    <template v-if="!loading && fund">
      <!-- Red stripe -->
      <div class="band-red-stripe"></div>

      <!-- Main content -->
      <div class="band band-host">
        <div class="band-content">

          <div class="mb-4">
            <v-btn
              variant="text"
              color="primary"
              prepend-icon="mdi-arrow-left"
              :to="`/for/${slug}`"
            >
              Back to memory
            </v-btn>
          </div>

          <!-- Charity info card -->
          <v-card class="main-card mb-6" elevation="0">
            <v-card-text class="pa-6">
              <div class="charity-header mb-4">
                <v-icon color="primary" size="32" class="mr-3">mdi-hand-heart-outline</v-icon>
                <h2 class="text-h5 charity-name">{{ fund.title }}</h2>
              </div>

              <p v-if="fund.charityDescription" class="text-body-1 mb-5 charity-description">
                {{ fund.charityDescription }}
              </p>

              <div v-if="fund.whyItMattered" class="why-it-mattered pa-4">
                <p class="text-body-2 text-medium-emphasis mb-1">
                  Why <strong>{{ memorial.nameOfPerson }}</strong> cared about {{ fund.title }}:
                </p>
                <p class="text-body-1 why-text">{{ fund.whyItMattered }}</p>
              </div>
            </v-card-text>
          </v-card>

          <!-- Recent donations -->
          <v-card class="main-card mb-6" elevation="0">
            <v-card-title class="section-title">
              Recent Donations
            </v-card-title>
            <v-card-text class="pa-0">
              <div v-if="fund.recentDonations && fund.recentDonations.length > 0">
                <div
                  v-for="(donation, idx) in fund.recentDonations"
                  :key="donation.id"
                  class="donation-row pa-4"
                  :class="{ 'donation-row--odd': idx % 2 === 0 }"
                >
                  <div class="donation-row-left">
                    <v-icon size="20" color="primary" class="mr-2">mdi-heart</v-icon>
                    <span class="donation-donor">
                      {{ donation.anon || !donation.donorName ? 'Anonymous' : donation.donorName }}
                    </span>
                  </div>
                  <div class="donation-row-right">
                    <span class="donation-amount">${{ Number(donation.amount).toFixed(2) }} AUD</span>
                    <span class="donation-date ml-3 text-body-2 text-medium-emphasis">{{ formatDate(donation.dateDonated) }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="pa-6 text-center text-medium-emphasis">
                <v-icon size="40" color="grey-lighten-1" class="mb-2">mdi-hand-heart-outline</v-icon>
                <p>No donations yet — be the first to contribute.</p>
              </div>
            </v-card-text>
          </v-card>

          <!-- Donate CTA -->
          <div v-if="!donateExpanded" class="text-center mb-6">
            <v-btn
              color="primary"
              size="large"
              rounded="0"
              elevation="0"
              class="px-10"
              prepend-icon="mdi-hand-heart"
              @click="donateExpanded = true"
            >
              Donate to {{ fund.title }}
            </v-btn>
          </div>

          <!-- Expanded donate section -->
          <v-expand-transition>
            <div v-if="donateExpanded">

              <!-- How donations work -->
              <v-card class="main-card mb-4" elevation="0">
                <v-card-title
                  class="section-title how-works-header"
                  style="cursor:pointer;"
                  @click="howWorksExpanded = !howWorksExpanded"
                >
                  <span>How donations work</span>
                  <v-spacer />
                  <v-icon>{{ howWorksExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-card-title>
                <v-expand-transition>
                  <v-card-text v-if="howWorksExpanded" class="pa-5">
                    <p class="text-body-1">
                      <span class="brand-name">PassingOn</span> has facilitated
                      <strong>{{ memorial.nameOfPerson }}</strong> to receive donations for
                      <strong>{{ fund.title }}</strong>.
                      PassingOn does not hold any money — Stripe is used to transfer your money directly to
                      <strong>{{ memorial.nameOfPerson }}</strong>, who will transfer all the donations to the charity when complete.
                    </p>
                  </v-card-text>
                </v-expand-transition>
              </v-card>

              <!-- Payment card -->
              <v-card class="main-card mb-6" elevation="0">
                <v-card-title class="section-title">
                  Make a Donation
                </v-card-title>
                <v-card-text class="pa-6">

                  <!-- Success state -->
                  <v-alert v-if="donationSuccess" type="success" variant="tonal" class="mb-4">
                    <strong>Thank you for your donation!</strong> Your contribution has been processed successfully.
                    <span v-if="sponsorshipSuccess"> Your monthly sponsorship of $4 has also been set up — thank you for helping keep this memory alive.</span>
                  </v-alert>

                  <template v-if="!donationSuccess">
                    <p class="text-body-1 mb-5">
                      How much would you like to donate to <strong>{{ fund.title }}</strong> (in AUD)?
                    </p>

                    <!-- Preset amounts -->
                    <div class="preset-amounts mb-4">
                      <v-btn
                        v-for="preset in presetAmounts"
                        :key="preset"
                        :variant="selectedPreset === preset ? 'flat' : 'outlined'"
                        :color="selectedPreset === preset ? 'primary' : 'default'"
                        class="preset-btn mr-2 mb-2"
                        rounded="0"
                        @click="selectPreset(preset)"
                      >
                        {{ preset === 'other' ? 'Other' : `$${preset}` }}
                      </v-btn>
                    </div>

                    <!-- Custom amount -->
                    <v-text-field
                      v-model="donationAmountRaw"
                      :label="`Amount (AUD)`"
                      variant="outlined"
                      type="number"
                      min="1"
                      step="1"
                      prefix="$"
                      class="mb-4"
                      style="max-width: 200px;"
                    />

                    <!-- Anonymous checkbox -->
                    <v-checkbox
                      v-model="donateAnonymously"
                      label="Donate anonymously (your name won't be shown publicly)"
                      class="mb-2"
                    />

                    <!-- Sponsorship option -->
                    <v-checkbox
                      v-model="wantToSponsor"
                      :label="`Sponsor ${memorial.nameOfPerson}'s online memory for $4 a month (after the first year)`"
                      class="mb-1"
                    />

                    <!-- Where a fully subscribed sponsorship ends up -->
                    <p v-if="wantToSponsor" class="text-body-2 text--secondary mb-3">
                      If this memory is already fully subscribed, your sponsorship is passed on to
                      <strong>{{ fund.title }}</strong> instead.
                    </p>

                    <!-- Sponsorship detail row -->
                    <div v-if="wantToSponsor" class="sponsorship-row mb-3">
                      <v-text-field
                        model-value="$4.00 / month"
                        label="Monthly sponsorship"
                        variant="outlined"
                        readonly
                        density="compact"
                        style="max-width: 200px;"
                        prepend-inner-icon="mdi-repeat"
                      />
                    </div>

                    <!-- Sponsorship T&C -->
                    <div v-if="wantToSponsor" class="mb-4">
                      <button
                        type="button"
                        class="tc-toggle text-caption text-primary"
                        @click="sponsorTcExpanded = !sponsorTcExpanded"
                      >
                        Terms and conditions
                        <v-icon size="14" class="ml-1">{{ sponsorTcExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                      </button>
                      <v-expand-transition>
                        <div v-if="sponsorTcExpanded" class="tc-body mt-2 pa-3 text-body-2">
                          When you choose to sponsor this online memory of <strong>{{ memorial.nameOfPerson }}</strong>,
                          your card will be deducted this amount until you choose to cancel.
                          Your $4 sponsorship is shared with other sponsors to cover the $12 monthly hosting cost.
                          You may cancel this at any point.
                        </div>
                      </v-expand-transition>
                    </div>

                    <!-- Stripe card element -->
                    <div class="mb-4">
                      <label class="text-body-2 text-medium-emphasis mb-2 d-block">Card details</label>
                      <div id="donate-card-element" class="stripe-card-element"></div>
                      <p v-if="stripeError" class="text-caption text-error mt-2">{{ stripeError }}</p>
                    </div>

                    <v-alert v-if="donationError" type="error" variant="tonal" class="mb-4">{{ donationError }}</v-alert>
                    <v-alert v-if="sponsorshipError" type="error" variant="tonal" class="mb-4">{{ sponsorshipError }}</v-alert>

                    <v-btn
                      color="primary"
                      size="large"
                      rounded="0"
                      elevation="0"
                      :loading="donationProcessing"
                      :disabled="donationProcessing || !donationAmountValid"
                      class="px-8"
                      @click="processDonation"
                    >
                      {{ wantToSponsor ? `Donate and Sponsor` : `Donate $${donationAmountDisplay} AUD` }}
                    </v-btn>
                  </template>

                </v-card-text>
              </v-card>

            </div>
          </v-expand-transition>

        </div>
      </div>
    </template>

    <!-- Not found state -->
    <template v-if="!loading && !fund && !error">
      <div class="band band-host">
        <div class="band-content text-center py-12">
          <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-hand-heart-outline</v-icon>
          <h3 class="text-h6 mb-2">No donation fund found</h3>
          <p class="text-body-2 text-medium-emphasis">This memory does not have a donation fund set up.</p>
          <v-btn variant="text" color="primary" :to="`/for/${slug}`" class="mt-4">Back to memory</v-btn>
        </div>
      </div>
    </template>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'
import { loadStripe } from '@stripe/stripe-js'
import ProfileMenu from '../components/ProfileMenu.vue'
import PassingOnLogo from '../components/PassingOnLogo.vue'

const route = useRoute()
const store = useMemorialStore()
const slug  = route.params.slug

const loading  = ref(true)
const error    = ref('')
const memorial = ref(null)
const fund     = ref(null)

const donateExpanded   = ref(false)
const howWorksExpanded = ref(false)

const presetAmounts    = [10, 20, 50, 100, 'other']
const selectedPreset   = ref(20)
const donationAmountRaw = ref('20')
const donateAnonymously = ref(false)
const donationError    = ref('')
const donationProcessing = ref(false)
const donationSuccess  = ref(false)
const stripeError      = ref('')

const wantToSponsor    = ref(false)
const sponsorTcExpanded = ref(false)
const sponsorshipError  = ref('')
const sponsorshipSuccess = ref(false)

let stripeInstance = null
let cardElement    = null

const isAuthenticated = computed(() => !!store.apiToken)

const donationAmountValid = computed(() => {
  const v = parseFloat(donationAmountRaw.value)
  return !isNaN(v) && v >= 1
})

const donationAmountDisplay = computed(() => {
  const v = parseFloat(donationAmountRaw.value)
  return isNaN(v) ? '0' : v.toFixed(2)
})

const selectPreset = (preset) => {
  selectedPreset.value = preset
  if (preset !== 'other') {
    donationAmountRaw.value = String(preset)
  }
}

const formatDate = (ts) => {
  if (!ts) return ''
  return new Date(ts).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })
}

const loadData = async () => {
  try {
    const res = await apiService.getPublicMemorial(slug)
    if (res.success && res.memorial) {
      memorial.value = res.memorial
      fund.value = res.memorial.memorialFunds?.[0] || null
    } else {
      error.value = res.msg || 'Memorial not found.'
    }
  } catch (e) {
    error.value = 'Unable to load donation page. Please try again.'
  } finally {
    loading.value = false
  }
}

const initStripe = async () => {
  await nextTick()
  const key = process.env.VUE_APP_STRIPE_PUBLIC_KEY
  if (!key) return
  stripeInstance = await loadStripe(key)
  if (!stripeInstance) return
  const elements = stripeInstance.elements()
  cardElement = elements.create('card', {
    hidePostalCode: true,
    style: {
      base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } }
    }
  })
  cardElement.mount('#donate-card-element')
  cardElement.on('change', (event) => {
    stripeError.value = event.error ? event.error.message : ''
  })
}

const processDonation = async () => {
  if (!donationAmountValid.value || !cardElement || !stripeInstance) return
  donationProcessing.value = true
  donationError.value = ''
  sponsorshipError.value = ''

  try {
    // Create the PaymentMethod once — reused for both donation and sponsorship
    const { error: pmError, paymentMethod } = await stripeInstance.createPaymentMethod({
      type: 'card',
      card: cardElement
    })
    if (pmError) {
      donationError.value = pmError.message || 'Card error. Please check your details.'
      return
    }

    // Process the one-time donation (Stripe Connect → host's account)
    const donRes = await apiService.processDonation({
      memorialSlug   : slug,
      amount         : parseFloat(donationAmountRaw.value),
      anonymous      : donateAnonymously.value,
      paymentMethodId: paymentMethod.id
    }, store.apiToken)

    if (donRes.success) {
      donationSuccess.value = true
      if (fund.value) {
        fund.value.recentDonations = [
          {
            id         : donRes.donation?.id,
            amount     : parseFloat(donationAmountRaw.value),
            dateDonated: Date.now(),
            anon       : donateAnonymously.value,
            donorName  : donateAnonymously.value ? null : (store.user ? `${store.user.firstName || ''} ${store.user.surname || ''}`.trim() || null : null)
          },
          ...(fund.value.recentDonations || [])
        ]
      }
    } else {
      donationError.value = donRes.msg || 'Payment failed. Please try again.'
      return
    }

    // If sponsorship was requested, process the $4/month subscription separately
    // (platform account, not Connect) using the same PaymentMethod
    if (wantToSponsor.value) {
      const subRes = await apiService.createSponsorship({
        memorialSlug   : slug,
        paymentMethodId: paymentMethod.id
      }, store.apiToken)

      if (subRes.success) {
        sponsorshipSuccess.value = true
      } else if (subRes.requiresAction && subRes.clientSecret) {
        // 3D Secure required — ask Stripe to confirm on the client side
        const { error: confirmError } = await stripeInstance.confirmCardPayment(subRes.clientSecret)
        if (confirmError) {
          sponsorshipError.value = `Sponsorship authentication failed: ${confirmError.message}. Your donation was still processed.`
        } else {
          sponsorshipSuccess.value = true
        }
      } else {
        sponsorshipError.value = (subRes.msg || 'Sponsorship setup failed.') + ' Your donation was still processed.'
      }
    }

  } catch (e) {
    donationError.value = 'An error occurred. Please try again.'
  } finally {
    donationProcessing.value = false
  }
}

// Watch for the donate section expanding to init Stripe
const onDonateExpand = async () => {
  if (donateExpanded.value && !stripeInstance) {
    await initStripe()
  }
}

watch(donateExpanded, onDonateExpand)

onMounted(loadData)
</script>

<style scoped>
.donations-page {
  min-height: 100vh;
  background: var(--v-theme-background);
}

.band-hero {
  background: #f5f0e8;
  padding: 20px 0;
}

.band-red-stripe {
  height: 6px;
  background: #c0392b;
}

.band-host {
  background: #fff;
  padding: 32px 0;
}

.band-content {
  max-width: 760px;
  margin: 0 auto;
  padding: 0 16px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 760px;
  margin: 0 auto;
  padding: 0 16px;
}

.brand-header {
  display: flex;
  align-items: center;
}

.brand-logo-svg {
  height: 36px;
}

.main-card {
  border: 1px solid rgba(0,0,0,0.12) !important;
  border-radius: 8px !important;
}

.section-title {
  font-weight: 600 !important;
  font-size: 1rem !important;
  border-bottom: 1px solid rgba(0,0,0,0.08);
  padding: 14px 20px;
}

.how-works-header {
  user-select: none;
}

.charity-header {
  display: flex;
  align-items: center;
}

.charity-name {
  font-weight: 700;
  color: #1a1a2e;
}

.charity-description {
  color: #444;
  line-height: 1.7;
}

.why-it-mattered {
  background: #f9f9f9;
  border-left: 4px solid var(--v-theme-primary, #7bd6dd);
  border-radius: 4px;
}

.why-text {
  color: #333;
  line-height: 1.7;
  font-style: italic;
}

.donation-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}

.donation-row--odd {
  background: #fafafa;
}

.donation-row-left {
  display: flex;
  align-items: center;
}

.donation-donor {
  font-weight: 500;
}

.donation-row-right {
  display: flex;
  align-items: center;
}

.donation-amount {
  font-weight: 700;
  color: var(--v-theme-primary, #7bd6dd);
}

.preset-amounts {
  display: flex;
  flex-wrap: wrap;
}

.preset-btn {
  min-width: 64px !important;
}

.stripe-card-element {
  border: 1px solid rgba(0,0,0,0.38);
  border-radius: 4px;
  padding: 14px 12px;
  background: #fff;
}

.sponsorship-row {
  padding-left: 36px;
}

.tc-toggle {
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 0;
  color: var(--v-theme-primary);
}

.tc-body {
  background: #f5f5f5;
  border-left: 3px solid var(--v-theme-primary, #7bd6dd);
  border-radius: 4px;
  color: #444;
  line-height: 1.6;
}

.brand-name {
  color: var(--v-theme-primary, #7bd6dd);
  font-weight: 700;
}
</style>
